import React, { useState, Fragment, useEffect, memo } from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useSelector, useDispatch } from "react-redux";
import ContentLoader from "react-content-loader";

import citroen from "~images/vehiculoMarcaSelector/citroen.svg";
import fiat from "~images/vehiculoMarcaSelector/fiat.svg";
import ford from "~images/vehiculoMarcaSelector/ford.svg";
import honda from "~images/vehiculoMarcaSelector/honda.svg";
import peugeot from "~images/vehiculoMarcaSelector/peugeot.svg";
import renault from "~images/vehiculoMarcaSelector/renault.svg";
import toyota from "~images/vehiculoMarcaSelector/toyota.svg";
import vw from "~images/vehiculoMarcaSelector/volkswagen.svg";
import chevrolet from "~images/vehiculoMarcaSelector/chevrolet.svg";

import filterItems from "~libs/filterItems";
import { fetchVehiculosMarcas } from "./actions";
import ListView from "~styled/listView/ListView";
import SearcherList from "~styled/searcherList/SearcherList";

const PREFIX = "VehiculoMarcaSelector";
const classes = {
  root: `${PREFIX}-root`,
  searcher: `${PREFIX}-searcher`,
  brandSection: `${PREFIX}-brandSection`,
  brandRow: `${PREFIX}-brandRow`,
  brandItemContainer: `${PREFIX}-brandItemContainer`,
  button: `${PREFIX}-button`,
  buttonContent: `${PREFIX}-buttonContent`,
  brandItemLabel: `${PREFIX}-brandItemLabel`,
  iconContainer: `${PREFIX}-iconContainer`,
  brandIcon: `${PREFIX}-brandIcon`,
  chip: `${PREFIX}-chip`,
};

const Root = styled("div")(({ theme }) => ({
  [`&.${classes.root}`]: {
    width: "100%",
  },
  [`.${classes.searcher}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    justifyContent: "space-around",
  },
  [`.${classes.brandSection}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
  },
  [`.${classes.brandRow}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  [`.${classes.button}`]: {
    marginTop: 10,
    width: "30%",
    height: 105,
    padding: 0,
    borderRadius: 8,
    background: theme.colours.white,
    boxShadow: "2px 2px 10px 0 #CCCCE4",
    marginBottom: "5%",

    [theme.breakpoints.up("desktop")]: {
      minWidth: 140,
      minHeight: 140,
    },
    "&.MuiButton-root, &.MuiButton-contained, &.MuiButton-containedPrimary": {
      background: theme.colours.white,
      boxShadow: "2px 2px 10px 0 #CCCCE4",
    },
    "&.MuiButton-contained:hover, &.MuiButton-containedPrimary:hover": {
      background: "#D5D5D5",
      boxShadow: "2px 2px 14px 0 #C9C9DF",
    },
    // subí especificidad para ganarle a disableElevation y estilos de MUI
    "&&": {
      background: theme.colours.white,
      boxShadow: "2px 2px 10px 0 #CCCCE4",
    },
    "&&:hover": {
      background: "#D5D5D5", // gris hover como en dev
      boxShadow: "2px 2px 14px 0 #C9C9DF",
    },
  },

  [`.${classes.buttonContent}`]: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
    [theme.breakpoints.up("desktop")]: {
      height: "100%",
    },
  },

  [`.${classes.iconContainer}`]: {
    justifyContent: "center",
    height: "71%",
    [theme.breakpoints.up("desktop")]: {
      display: "flex",
      justifyContent: "center",
      height: "81%",
      minHeight: 105,
      alignItems: "center",
    },
  },

  [`.${classes.brandItemLabel}`]: {
    marginBottom: -1,
    borderRadius: "0 0 8px 8px",
    lineHeight: "35px",
    color: theme.colours.brownGrey,
    background: "#e4e4e6",
    fontSize: 13,
    textTransform: "lowercase",
    "&:first-letter": { textTransform: "uppercase" },
  },

  [`.${classes.brandIcon}`]: {
    objectFit: "contain",
    padding: 2,
    paddingTop: 10,
    paddingBottom: 10,
    width: 70,
    height: 70,
    marginTop: 4,
    marginBottom: -4,
  },

  [`.${classes.chip}`]: {
    borderRadius: 6,
    fontWeight: "bold",
    margin: 2,
  },
}));
interface Marca {
  label: string;
  value: string;
}

interface ReduxState {
  vehiculoMarcasReducer: {
    marcas: Marca[];
    isLoading: boolean;
  };
}

interface VehiculoMarcaSelectorProps {
  value?: { label: string | null; value: string | null };
  onChange?: (event: {
    target: { value: { label: string | null; value: string | null } };
  }) => void;
}

const useReduxSelector = () =>
  useSelector((state: ReduxState) => ({
    marcas: state.vehiculoMarcasReducer.marcas,
    isLoading: state.vehiculoMarcasReducer.isLoading,
  }));

const VehiculoMarcaSelector: React.FC<VehiculoMarcaSelectorProps> = memo(
  ({ value = { label: null, value: null }, onChange }) => {
    const { marcas, isLoading } = useReduxSelector();
    const dispatch = useDispatch();

    useEffect(() => {
      dispatch(fetchVehiculosMarcas());
    }, [dispatch]);

    const [filter, setFilter] = useState<string>("");

    const handleBrandItem = (item: { label: string; value: string | null }) => {
      setFilter("");
      onChange?.({
        target: { value: { label: item.label, value: item.value } },
      });
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.value);
    };
    return (
      <Root className={classes.root}>
        <Fragment>
          {!value.value && (
            <div className={classes.searcher}>
              <SearcherList
                onClear={() => setFilter("")}
                filter={filter}
                onChange={handleFilterChange}
                placeholder={"Buscar marca"}
                borderBottomRounded={true}
              />
              {filter === "" ? (
                <div style={{ paddingTop: 28 }}>
                  <Typography
                    variant="overline"
                    display="block"
                    style={{ color: "#a7a7a7" }}
                  >
                    MARCAS DESTACADAS
                  </Typography>
                  <div className={classes.brandSection}>
                    <div className={classes.brandRow}>
                      <BrandItem
                        icon={chevrolet}
                        value={{ value: "CHEVROLET", label: "CHEVROLET" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                      <BrandItem
                        icon={citroen}
                        value={{ value: "CITROEN", label: "CITROEN" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                      <BrandItem
                        icon={fiat}
                        value={{ value: "FIAT", label: "FIAT" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                    </div>
                    <div className={classes.brandRow}>
                      <BrandItem
                        icon={renault}
                        value={{ value: "RENAULT", label: "RENAULT" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                      <BrandItem
                        icon={vw}
                        value={{ value: "VOLKSWAGEN", label: "VOLKSWAGEN" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                      <BrandItem
                        icon={peugeot}
                        value={{ value: "PEUGEOT", label: "PEUGEOT" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                    </div>
                    <div className={classes.brandRow}>
                      <BrandItem
                        icon={ford}
                        value={{ value: "FORD", label: "FORD" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                      <BrandItem
                        icon={toyota}
                        value={{ value: "TOYOTA", label: "TOYOTA" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                      <BrandItem
                        icon={honda}
                        value={{ value: "HONDA", label: "HONDA" }}
                        onClick={handleBrandItem}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <ListView
                    isLoading={isLoading}
                    onItemSelected={handleBrandItem}
                    list={filterItems(marcas, filter)}
                  />
                </div>
              )}
            </div>
          )}
        </Fragment>
      </Root>
    );
  }
);
export default VehiculoMarcaSelector;

interface BrandItemProps {
  isLoading: boolean;
  icon: string;
  value: { label: string; value: string };
  onClick: (item: { label: string; value: string }) => void;
}

const BrandItem: React.FC<BrandItemProps> = ({
  isLoading,
  icon,
  value,
  onClick,
}) => {
  if (isLoading) {
    return (
      <div className={classes.button}>
        <div className={classes.buttonContent}>
          <div className={classes.iconContainer}>
            <ContentLoader
              height={"80%"}
              width={"80%"}
              speed={1}
              backgroundColor="#f7f8ff"
              foregroundColor="#d9e8df"
            >
              <rect x="12" y="12" rx="4" ry="4" width="80%" height="80%" />
            </ContentLoader>
          </div>
          <div style={{ height: 54 }} className={classes.brandItemLabel} />
        </div>
      </div>
    );
  }

  return (
    <Button
      variant="contained"
      color="inherit"
      //disableElevation
      //disableRipple
      className={classes.button}
      onClick={() => onClick(value)}
    >
      <div className={classes.buttonContent}>
        <div className={classes.iconContainer}>
          <img src={icon} className={classes.brandIcon} alt="" />
        </div>
        <Typography component="div" className={classes.brandItemLabel}>
          {value.label ? value.label : ""}
        </Typography>
      </div>
    </Button>
  );
};
